import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useMutation } from '@apollo/client';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Typography
} from '@mui/material';
import {
  Add,
  ArrowDownward,
  ArrowUpward,
  CloudUpload,
  DeleteOutline,
  PlayCircleOutline,
  SelfImprovement,
  Sync,
  Videocam
} from '@mui/icons-material';
import { UPLOAD_PLAY_LIBRARY_MEDIA } from 'api/graphql/playMusic';
import VideoRecorderModal from 'components/play/admin/VideoRecorderModal';
import VideoPreviewModal from 'components/play/admin/VideoPreviewModal';
import VideoTrimField from 'components/play/admin/VideoTrimField';
import ConfirmDeleteModal from 'components/shared/modals/ConfirmDeleteModal';
import { validateVideoFile } from 'components/play/admin/mediaLimits';
import { createSnack } from 'common/utils/general';
import uiSlice from 'store/modules/ui';

/**
 * Segundos que dura REALMENTE lo que se va a ver de un video: el tramo recortado si hay marcas,
 * y si no la duración del archivo. Devuelve 0 cuando todavía no se conoce nada.
 */
const clipSeconds = (step) => {
  const start = typeof step.video_start_ms === 'number' ? step.video_start_ms : 0;
  // Sin marca de fin manda la duración del archivo; si tampoco se conoce, no hay nada que comparar.
  let end = null;
  if (typeof step.video_end_ms === 'number') end = step.video_end_ms;
  else if (typeof step.video_duration_ms === 'number') end = step.video_duration_ms;

  if (end === null || end <= start) return 0;
  return Math.round((end - start) / 1000);
};

// A partir de esta diferencia se avisa. Un par de segundos de desfase no se percibe; 5 sí, y es
// cuando el video empieza a repetirse o a cortarse a media postura.
const SYNC_TOLERANCE_SECONDS = 3;

// Botones gemelos "Grabar" / "Subir" de cada paso: acción secundaria, sin protagonismo.
const stepActionSx = {
  flex: 'none',
  textTransform: 'none',
  fontWeight: 700,
  minWidth: 0,
  transition: 'color 200ms cubic-bezier(0.32,0.72,0,1)',
  '&:active': { transform: 'scale(0.98)' }
};

/**
 * OTRIX Play (admin) — constructor de la SECUENCIA de una práctica guiada: filas
 * reordenables (botones, sin DnD) con tipo, duración, textos y video por paso. Esto es lo
 * que hace dinámica la práctica de la entrenadora: cada paso dura lo que su video/pausa pida.
 */
function PlayRecoveryStepEditor({ steps, onChange, practiceKey = null }) {
  const { t } = useTranslation(['play']);
  const dispatch = useDispatch();
  const fileRef = useRef(null);
  const [uploadingIndex, setUploadingIndex] = useState(null);
  const [recorderIndex, setRecorderIndex] = useState(null);
  const [previewIndex, setPreviewIndex] = useState(null);
  const [removeIndex, setRemoveIndex] = useState(null);
  const pendingIndexRef = useRef(null);
  const [uploadMedia] = useMutation(UPLOAD_PLAY_LIBRARY_MEDIA);

  const update = (index, patch) =>
    onChange(steps.map((s, i) => (i === index ? { ...s, ...patch } : s)));

  const move = (index, delta) => {
    const target = index + delta;
    if (target < 0 || target >= steps.length) return;
    const next = [...steps];
    [next[index], next[target]] = [next[target], next[index]];
    onChange(next);
  };

  const remove = (index) => onChange(steps.filter((_, i) => i !== index));

  const add = (type) =>
    onChange([
      ...steps,
      {
        key: null,
        title: '',
        description: '',
        type,
        duration_seconds: type === 'rest' ? 30 : 60,
        video_url: null,
        image_url: null,
        video_start_ms: null,
        video_end_ms: null,
        video_duration_ms: null,
        video_has_audio: false
      }
    ]);

  const notify = (variant, message) =>
    dispatch(uiSlice.actions.enqueueSnackbar(createSnack({ key: `step-media-${Date.now()}`, variant, message })));

  const pickVideo = (index) => {
    pendingIndexRef.current = index;
    fileRef.current?.click();
  };

  // Camino único del video del paso: grabado en la app o elegido del explorador de archivos.
  const uploadForStep = async (index, file) => {
    // Peso y duración se revisan ANTES de subir (los videos de teléfono son enormes).
    const problem = await validateVideoFile(file, t);
    if (problem) {
      notify('error', problem);
      return;
    }

    setUploadingIndex(index);
    try {
      const { data } = await uploadMedia({ variables: { file } });
      // Las marcas del video ANTERIOR no valen para el nuevo archivo: dejarlas puestas haría
      // que el paso arrancara en un segundo que quizá ya no existe (recuadro en negro).
      update(index, {
        video_url: data?.uploadPlayLibraryMedia?.url || null,
        video_start_ms: null,
        video_end_ms: null,
        video_duration_ms: null
      });
    } catch {
      notify('error', t('recoveryAdmin.videoError', { ns: 'play', defaultValue: 'No pudimos subir el video.' }));
    } finally {
      setUploadingIndex(null);
    }
  };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    const index = pendingIndexRef.current;
    pendingIndexRef.current = null;
    if (!file || index === null) return;
    await uploadForStep(index, file);
  };

  // Quitar el video se lleva TAMBIÉN su recorte: un start/end huérfano se aplicaría al video
  // general de la práctica, que es otro archivo.
  const removeVideo = () => {
    if (removeIndex === null) return;
    update(removeIndex, {
      video_url: null,
      video_start_ms: null,
      video_end_ms: null,
      video_duration_ms: null,
      video_has_audio: false
    });
    setRemoveIndex(null);
  };

  const stepLabel = (step) => {
    if (step.type === 'rest') return t('recovery.steps.rest', { ns: 'play', defaultValue: 'Descanso' });
    if (step.key && practiceKey) return t(`recovery.blocks.${practiceKey}.steps.${step.key}`, { ns: 'play' });
    return null;
  };

  return (
    <Stack spacing={1.5}>
      <input ref={fileRef} type="file" accept="video/*,image/*" hidden onChange={onFile} />

      {steps.map((step, index) => {
        const systemLabel = stepLabel(step);
        const isRest = step.type === 'rest';
        return (
          <Stack
            // eslint-disable-next-line react/no-array-index-key
            key={`${step.key || 'step'}-${index}`}
            spacing={1.25}
            sx={{
              p: 1.5,
              borderRadius: '12px',
              border: '1px solid',
              borderColor: 'hairline.main',
              backgroundColor: isRest ? 'surface.overlay' : 'surface.raised'
            }}>
            <Stack direction="row" spacing={1} alignItems="center">
              <SelfImprovement sx={{ fontSize: 16, color: isRest ? 'text.disabled' : 'gold.main', flex: 'none' }} />
              <Typography variant="caption" fontWeight={800} sx={{ flex: 1, minWidth: 0 }} noWrap>
                {`${index + 1}. ${systemLabel || step.title || t('recoveryAdmin.stepUntitled', { ns: 'play', defaultValue: 'Paso sin título' })}`}
              </Typography>
              <IconButton size="small" onClick={() => move(index, -1)} disabled={index === 0} aria-label={t('recoveryAdmin.moveUp', { ns: 'play', defaultValue: 'Subir' })}>
                <ArrowUpward sx={{ fontSize: 15 }} />
              </IconButton>
              <IconButton size="small" onClick={() => move(index, 1)} disabled={index === steps.length - 1} aria-label={t('recoveryAdmin.moveDown', { ns: 'play', defaultValue: 'Bajar' })}>
                <ArrowDownward sx={{ fontSize: 15 }} />
              </IconButton>
              <IconButton
                size="small"
                onClick={() => remove(index)}
                aria-label={t('recoveryAdmin.removeStep', { ns: 'play', defaultValue: 'Quitar paso' })}
                sx={{ color: 'sessionStatus.absent.fg' }}>
                <DeleteOutline sx={{ fontSize: 16 }} />
              </IconButton>
            </Stack>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25}>
              <TextField
                select
                size="small"
                label={t('recoveryAdmin.stepType', { ns: 'play', defaultValue: 'Tipo' })}
                value={step.type}
                onChange={(e) => update(index, { type: e.target.value })}
                sx={{ minWidth: 130 }}>
                <MenuItem value="exercise">{t('recoveryAdmin.typeExercise', { ns: 'play', defaultValue: 'Ejercicio' })}</MenuItem>
                <MenuItem value="rest">{t('recoveryAdmin.typeRest', { ns: 'play', defaultValue: 'Descanso' })}</MenuItem>
              </TextField>
              <TextField
                size="small"
                type="number"
                label={t('recoveryAdmin.stepSeconds', { ns: 'play', defaultValue: 'Segundos' })}
                value={step.duration_seconds}
                onChange={(e) => update(index, { duration_seconds: Math.max(5, Number(e.target.value) || 5) })}
                inputProps={{ min: 5, max: 3600 }}
                sx={{ minWidth: 110 }}
              />
              {!isRest && !systemLabel && (
                <TextField
                  size="small"
                  label={t('recoveryAdmin.stepTitle', { ns: 'play', defaultValue: 'Título del paso' })}
                  value={step.title || ''}
                  onChange={(e) => update(index, { title: e.target.value })}
                  fullWidth
                />
              )}
            </Stack>

            {!isRest && (
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25} alignItems={{ sm: 'center' }}>
                {!systemLabel && (
                  <TextField
                    size="small"
                    label={t('recoveryAdmin.stepDesc', { ns: 'play', defaultValue: 'Explicación (qué hacer)' })}
                    value={step.description || ''}
                    onChange={(e) => update(index, { description: e.target.value })}
                    fullWidth
                    multiline
                    maxRows={2}
                  />
                )}
                <Stack direction="row" spacing={0.5} alignItems="center" sx={{ flex: 'none' }}>
                  <Button
                    size="small"
                    startIcon={uploadingIndex === index ? <CircularProgress size={12} color="inherit" /> : <Videocam sx={{ fontSize: 16 }} />}
                    onClick={() => setRecorderIndex(index)}
                    disabled={uploadingIndex !== null}
                    sx={{ ...stepActionSx, color: step.video_url ? 'gold.main' : 'text.secondary' }}>
                    {t('recoveryAdmin.videoRecord', { ns: 'play', defaultValue: 'Grabar' })}
                  </Button>
                  <Button
                    size="small"
                    startIcon={<CloudUpload sx={{ fontSize: 16 }} />}
                    onClick={() => pickVideo(index)}
                    disabled={uploadingIndex !== null}
                    sx={{ ...stepActionSx, color: step.video_url ? 'gold.main' : 'text.secondary' }}>
                    {t('recoveryAdmin.videoPick', { ns: 'play', defaultValue: 'Subir archivo' })}
                  </Button>
                  {/* Sin esto no había forma de comprobar que el archivo subido era el correcto
                      ni de deshacerse de él: solo se podía reemplazar por otro. */}
                  {step.video_url && (
                    <>
                      <Button
                        size="small"
                        startIcon={<PlayCircleOutline sx={{ fontSize: 16 }} />}
                        onClick={() => setPreviewIndex(index)}
                        sx={{ ...stepActionSx, color: 'text.secondary' }}>
                        {t('recoveryAdmin.videoView', { ns: 'play', defaultValue: 'Ver' })}
                      </Button>
                      <IconButton
                        size="small"
                        onClick={() => setRemoveIndex(index)}
                        aria-label={t('recoveryAdmin.videoRemove', { ns: 'play', defaultValue: 'Quitar el video' })}
                        sx={{ color: 'sessionStatus.absent.fg' }}>
                        <DeleteOutline sx={{ fontSize: 16 }} />
                      </IconButton>
                    </>
                  )}
                </Stack>
              </Stack>
            )}

            {/* Sincronía cronómetro ↔ video. Sin esto el paso dura lo que el admin escribió y el
                video lo que dura, así que un tramo de 8 s dentro de un paso de 100 s se repetía
                doce veces y uno de 120 s se cortaba a media postura. */}
            {!isRest && step.video_url && clipSeconds(step) > 0 && (
              (() => {
                const clip = clipSeconds(step);
                const diff = Math.abs(clip - (step.duration_seconds || 0));
                const synced = diff <= SYNC_TOLERANCE_SECONDS;
                return (
                  <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={1}
                    alignItems={{ sm: 'center' }}
                    sx={{ px: 0.5 }}>
                    <Typography
                      variant="caption"
                      sx={{ flex: 1, minWidth: 0, color: synced ? 'text.disabled' : 'sessionStatus.pending.fg', fontVariantNumeric: 'tabular-nums' }}>
                      {synced
                        ? t('recoveryAdmin.syncOk', {
                            ns: 'play',
                            seconds: clip,
                            defaultValue: 'El paso y el video duran lo mismo ({{seconds}} s).'
                          })
                        : t('recoveryAdmin.syncOff', {
                            ns: 'play',
                            step: step.duration_seconds || 0,
                            clip,
                            defaultValue: 'El paso dura {{step}} s y el video {{clip}} s: se repetirá o se cortará.'
                          })}
                    </Typography>
                    {!synced && (
                      <Button
                        size="small"
                        startIcon={<Sync sx={{ fontSize: 16 }} />}
                        onClick={() => update(index, { duration_seconds: Math.max(5, Math.min(3600, clip)) })}
                        sx={{ ...stepActionSx, color: 'gold.main' }}>
                        {t('recoveryAdmin.syncApply', { ns: 'play', defaultValue: 'Igualar al video' })}
                      </Button>
                    )}
                  </Stack>
                );
              })()
            )}

            {!isRest && step.video_url && (
              <VideoTrimField
                videoUrl={step.video_url}
                startMs={step.video_start_ms}
                endMs={step.video_end_ms}
                durationMs={step.video_duration_ms}
                hasAudio={step.video_has_audio}
                onChange={(patch) =>
                  update(index, {
                    ...('startMs' in patch ? { video_start_ms: patch.startMs } : {}),
                    ...('endMs' in patch ? { video_end_ms: patch.endMs } : {}),
                    ...('durationMs' in patch ? { video_duration_ms: patch.durationMs } : {}),
                    ...('hasAudio' in patch ? { video_has_audio: patch.hasAudio } : {})
                  })
                }
              />
            )}
          </Stack>
        );
      })}

      <Stack direction="row" spacing={1}>
        <Button size="small" startIcon={<Add />} onClick={() => add('exercise')} sx={{ color: 'gold.main', textTransform: 'none', fontWeight: 700 }}>
          {t('recoveryAdmin.addStep', { ns: 'play', defaultValue: 'Agregar ejercicio' })}
        </Button>
        <Button size="small" startIcon={<Add />} onClick={() => add('rest')} sx={{ color: 'text.secondary', textTransform: 'none', fontWeight: 700 }}>
          {t('recoveryAdmin.addRest', { ns: 'play', defaultValue: 'Agregar descanso' })}
        </Button>
      </Stack>

      {steps.length > 0 && (
        <Box sx={{ px: 0.5 }}>
          <Typography variant="caption" sx={{ color: 'text.disabled', fontVariantNumeric: 'tabular-nums' }}>
            {t('recoveryAdmin.totalSeconds', {
              ns: 'play',
              count: steps.reduce((acc, s) => acc + (s.duration_seconds || 0), 0),
              defaultValue: 'Duración total de la secuencia: {{count}} s'
            })}
          </Typography>
        </Box>
      )}

      <VideoPreviewModal
        open={previewIndex !== null}
        src={previewIndex !== null ? steps[previewIndex]?.video_url : null}
        startMs={previewIndex !== null ? steps[previewIndex]?.video_start_ms : null}
        endMs={previewIndex !== null ? steps[previewIndex]?.video_end_ms : null}
        hasAudio={previewIndex !== null ? Boolean(steps[previewIndex]?.video_has_audio) : false}
        title={t('recoveryAdmin.previewStep', {
          ns: 'play',
          position: (previewIndex ?? 0) + 1,
          defaultValue: 'Video del paso {{position}}'
        })}
        onClose={() => setPreviewIndex(null)}
      />

      <ConfirmDeleteModal
        open={removeIndex !== null}
        onClose={() => setRemoveIndex(null)}
        onConfirm={removeVideo}
        title={t('recoveryAdmin.videoRemoveTitle', { ns: 'play', defaultValue: 'Quitar el video' })}
        message={t('recoveryAdmin.videoRemoveMessage', {
          ns: 'play',
          defaultValue: 'El paso volverá a usar el video general de la práctica, si tiene uno.'
        })}
        confirmLabel={t('recoveryAdmin.videoRemove', { ns: 'play', defaultValue: 'Quitar el video' })}
      />

      <VideoRecorderModal
        open={recorderIndex !== null}
        onClose={() => setRecorderIndex(null)}
        onRecorded={(file) => uploadForStep(recorderIndex, file)}
        title={t('recoveryAdmin.recorder.titleStep', {
          ns: 'play',
          position: (recorderIndex ?? 0) + 1,
          defaultValue: 'Grabar video del paso {{position}}'
        })}
      />
    </Stack>
  );
}

PlayRecoveryStepEditor.propTypes = {
  steps: PropTypes.array.isRequired,
  onChange: PropTypes.func.isRequired,
  // Esta versión de `require-default-props` todavía exige `defaultProps`, que React 18.3 ya
  // deprecó en componentes de función: el valor por defecto va en la firma.
  // eslint-disable-next-line react/require-default-props
  practiceKey: PropTypes.string
};

export default PlayRecoveryStepEditor;
